import { useEffect, useState } from "react";
import { CheckCircle2, Circle, ClipboardCheck } from "lucide-react";
import api from "../../lib/api";

/* Spec §10 — Trip tasks widget. Lists tasks applied from prep templates and
   lets the traveler tick them off. Groups by category. */

const CAT_LABELS = {
  documents: "Documents",
  prep:      "Preparation",
  entry:     "Arrival & entry",
};

export default function TripTasksWidget({ tripId, reloadKey }) {
  const [tasks, setTasks] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () =>
    api.get(`/prep-templates/trips/${tripId}/tasks`).then((r) => setTasks(r.data)).catch(() => setTasks({ items: [], total: 0, done: 0 }));

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tripId, reloadKey]);

  if (!tasks || tasks.total === 0) return null;

  const toggle = async (task) => {
    setBusy(task.id);
    try {
      await api.patch(`/prep-templates/tasks/${task.id}`, { done: !task.done });
      await load();
    } catch (e) { /* silent */ }
    finally { setBusy(null); }
  };

  const grouped = tasks.items.reduce((acc, t) => {
    const k = t.category || "prep";
    (acc[k] = acc[k] || []).push(t);
    return acc;
  }, {});

  const pct = tasks.total > 0 ? Math.round((tasks.done / tasks.total) * 100) : 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="trip-tasks">
      <div className="flex items-center gap-3 mb-3">
        <span className="w-10 h-10 rounded-xl bg-blue-500 text-white grid place-items-center shrink-0"><ClipboardCheck className="w-5 h-5" /></span>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-slate-500">Preparation tasks · §10</div>
          <div className="font-serif font-800 text-lg text-slate-900">{tasks.done} of {tasks.total} done</div>
        </div>
        <span className="font-800 text-blue-700 text-lg">{pct}%</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-4">
        <div className="h-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="space-y-4">
        {Object.entries(grouped).map(([cat, items]) => (
          <div key={cat}>
            <div className="text-[10px] font-800 uppercase tracking-widest text-slate-500 mb-1.5">{CAT_LABELS[cat] || cat}</div>
            <ul className="divide-y divide-slate-100">
              {items.map((t) => (
                <li key={t.id} className="flex items-start gap-2.5 py-2" data-testid={`task-${t.id}`}>
                  <button onClick={() => toggle(t)} disabled={busy === t.id}
                    className="shrink-0 mt-0.5" data-testid={`task-toggle-${t.id}`}>
                    {t.done ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Circle className="w-5 h-5 text-slate-300" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className={`text-sm ${t.done ? "line-through text-slate-400" : "text-slate-800"}`}>{t.label}</div>
                    {t.days_before && (
                      <div className="text-[10px] text-slate-500">{Math.abs(t.days_before)} days before trip</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
